
import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaFileExcel, FaTimes, FaSpinner } from "react-icons/fa";
import { format, subDays } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import "./CanReport.css";

const CANReport = () => {
  const [searchInput, setSearchInput] = useState("");
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [dateRange, setDateRange] = useState("Last Day");
  const [startDate, setStartDate] = useState(
    format(subDays(new Date(), 1), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch vehicles using TanStack Query
  const { data: vehicles = [], isLoading: vehiclesLoading } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const response = await fetch("https://api.yellowmatrix.co.in/vehicles", {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response.json();
    },
    onError: (err) => {
      setError("Failed to fetch vehicles: " + err.message);
    }
  });

  // Filter vehicles based on search input
  const filteredVehicles = searchInput.trim() === "" 
    ? vehicles 
    : vehicles.filter(vehicle => 
        vehicle.VehicleID.toLowerCase().includes(searchInput.toLowerCase())
      );

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const removeVehicle = (vehicleId) => {
    setSelectedVehicles(selectedVehicles.filter(v => v !== vehicleId));
    setSuccessMessage("");
  };

  const addVehicle = (vehicleId) => {
    if (!selectedVehicles.includes(vehicleId)) {
      setSelectedVehicles([vehicleId]); // Only allow one vehicle to be selected
      setSuccessMessage(`Vehicle ${vehicleId} selected. Click GENERATE REPORT to download data.`);
    }
    setSearchInput(""); // Clear search after selection
    setShowDropdown(false); // Hide dropdown after selection
  };

  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    setDateRange(value);

    const today = new Date();
    switch (value) {
      case "Last Day":
        setStartDate(format(subDays(today, 1), "yyyy-MM-dd"));
        setEndDate(format(today, "yyyy-MM-dd"));
        break;
      case "Last 7 Days":
        setStartDate(format(subDays(today, 7), "yyyy-MM-dd"));
        setEndDate(format(today, "yyyy-MM-dd"));
        break;
      case "Last 30 Days":
        setStartDate(format(subDays(today, 29), "yyyy-MM-dd"));
        setEndDate(format(today, "yyyy-MM-dd"));
      break;
      case "Custom":
        // Keep current dates for custom selection
        break;
      default:
        break;
    }
  };

  const handleReset = () => {
    setSearchInput("");
    setSelectedVehicles([]);
    setDateRange("Last Day");
    setStartDate(format(subDays(new Date(), 1), "yyyy-MM-dd"));
    setEndDate(format(new Date(), "yyyy-MM-dd"));
    setError("");
    setSuccessMessage("");
    setShowDropdown(false);
    setIsGenerating(false);
  };

  const formatDateForAPI = (dateStr) => {
    // Convert from yyyy-MM-dd to dd/MM/yyyy
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const downloadExcel = (blob, vehicleId, startDate, endDate) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `CAN_Data_${vehicleId}_${startDate}_${endDate}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const generateReport = async () => {
    if (selectedVehicles.length === 0) {
      setError("Please select a vehicle");
      return;
    }

    try {
      setError("");
      setIsGenerating(true);
      const token = localStorage.getItem("token");
      
      // Only one vehicle is selected, so we process just that one
      const vehicleId = selectedVehicles[0];
      const formattedStartDate = formatDateForAPI(startDate);
      const formattedEndDate = formatDateForAPI(endDate);
      
      const url = `https://api.yellowmatrix.co.in/canreport?vehicle_id=${vehicleId}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.Error || `Failed to fetch report for ${vehicleId}`);
      }
      
      // Check if response is Excel file
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
        const blob = await response.blob();
        downloadExcel(blob, vehicleId, formattedStartDate, formattedEndDate);
        setSuccessMessage(`Report for ${vehicleId} downloaded successfully!`);
      } else {
        // If not Excel, try to parse as JSON for error message
        const data = await response.json();
        console.warn(`Unexpected response format for ${vehicleId}:`, data);
        setError("Unexpected response format from server");
      }
    } catch (err) {
      setError("Failed to generate report: " + err.message);
      console.error("Error generating report:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="can-report-container">
      <h1 className="report-title">CAN Report</h1>
      <hr className="can-report-line" width="100%" />
      
      {error && <div className="can-error-message">{error}</div>}
      {successMessage && <div className="can-success-message">{successMessage}</div>}
      
      <div className="controls-container">
        <div className="search-container-can" ref={dropdownRef}>
          <FaSearch className="search-icon-can" />
          <input
            type="text"
            placeholder="Search Vehicles"
            className="search-input-can"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onFocus={handleSearchFocus}
          />
          
          {showDropdown && (
            <div className="can-vehicle-dropdown">
              {vehiclesLoading ? (
                // Skeleton loader for dropdown
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="can-vehicle-option skeleton-loader-can">
                    Loading...
                  </div>
                ))
              ) : filteredVehicles.length > 0 ? (
                filteredVehicles.map(vehicle => (
                  <div 
                    key={vehicle.VehicleID} 
                    className="can-vehicle-option"
                    onClick={() => addVehicle(vehicle.VehicleID)}
                  >
                    {vehicle.VehicleID}
                  </div>
                ))
              ) : (
                <div className="can-vehicle-option can-no-results">No vehicles found</div>
              )}
            </div>
          )}
        </div>

        <div className="date-range-container">
          <label className="control-label">Date Range</label>
          <select
            value={dateRange}
            onChange={handleDateRangeChange}
            className="date-range-select"
          >
            <option>Last Day</option>
            <option>Last 7 Days</option>
            <option>Last 30 Days</option>
            <option>Custom</option>
          </select>
        </div>

        <div className="date-input-container">
          <label className="control-label">Start Date:</label>
          <input
            type="date"
            className="date-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={dateRange !== "Custom"}
          />
        </div>

        <div className="date-input-container">
          <label className="control-label">End Date:</label>
          <input
            type="date"
            className="date-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={dateRange !== "Custom"}
          />
        </div>

        <button 
          className="generate-report-btn-can" 
          onClick={generateReport}
          disabled={vehiclesLoading || selectedVehicles.length === 0 || isGenerating}
        >
          {isGenerating ? (
            <>
              <FaSpinner className="spinner-icon" />
              DOWNLOADING...
            </>
          ) : (
            "GENERATE REPORT"
          )}
        </button>

        <button className="action-btn-can reset-btn" onClick={handleReset} disabled={isGenerating}>
          RESET
        </button>
        <Link to="/Report">
          <button className="action-btn-can back-btn" disabled={isGenerating}>
            BACK TO ALL REPORTS
          </button>
        </Link>
        <FaFileExcel className="excel-icon file-icon" title="Export to Excel" />
      </div>

      <div className="selected-vehicles-container">
        {selectedVehicles.map((vehicleId, idx) => (
          <span key={idx} className="vehicle-tag">
            {vehicleId}
            <FaTimes
              className="remove-icon-can"
              onClick={() => !isGenerating && removeVehicle(vehicleId)}
              title="Remove vehicle"
            />
          </span>
        ))}
        {selectedVehicles.length === 0 && (
          <div className="no-vehicles-message-can">
            {vehiclesLoading ? (
              <span className="skeleton-loader-can">Loading vehicles...</span>
            ) : (
              "No vehicle selected. Search and select a vehicle above."
            )}
          </div>
        )}
        {selectedVehicles.length === 1 && (
          <div className="selection-info-can">Only one vehicle can be selected at a time.</div>
        )}
      </div>
    </div>
  );
};

export default CANReport;